import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import {
  OnboardingService,
  WizardState,
  WizardStepKey,
} from '../../services/onboarding.service';
import { RealtimeService } from '../../services/realtime.service';
import { TicketMessage } from '../../services/support-tickets.service';
import { ToastService } from '../../ui/toast/toast.service';
import { VdAddressPickerComponent, AddressValue } from '../../ui';

/**
 * CompleteSignup — wizard de signup vendeur RESUMABLE.
 *
 * Pilote l'onboarding bout-en-bout (email, téléphone WhatsApp, profil, CGU
 * vendeur, validation support) à partir de `/me/onboarding/state` : à la
 * reconnexion, le vendeur ATTERRIT sur l'étape `next_step` (ou l'écran « en
 * validation »). L'écran de revue intègre la discussion temps réel avec
 * l'équipe support (badge non-lu + auto-ouverture sur nouveau message).
 */
@Component({
  selector: 'app-complete-signup',
  standalone: true,
  templateUrl: './complete-signup.component.html',
  styleUrls: ['./complete-signup.component.css'],
  imports: [CommonModule, FormsModule, VdAddressPickerComponent],
})
export class CompleteSignupComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly api = inject(OnboardingService);
  private readonly http = inject(HttpClient);
  private readonly cfg = inject(ConfigService);
  private readonly realtime = inject(RealtimeService);
  private readonly toast = inject(ToastService);

  // ── État global du wizard ───────────────────────────────────────
  readonly loading = signal<boolean>(true);
  readonly state = signal<WizardState | null>(null);
  readonly activeStep = signal<WizardStepKey>('email');

  readonly STEP_ORDER: WizardStepKey[] = ['email', 'phone', 'profile', 'contract', 'review'];

  // ── Étape email ─────────────────────────────────────────────────
  readonly emailResending = signal<boolean>(false);

  // ── Étape téléphone (OTP WhatsApp) ──────────────────────────────
  readonly otpPhone = signal<string>('');
  readonly otpCode = signal<string>('');
  readonly otpSent = signal<boolean>(false);
  readonly otpSending = signal<boolean>(false);
  readonly otpVerifying = signal<boolean>(false);

  // ── Étape profil ────────────────────────────────────────────────
  user: any = {
    user_type: 'individual',
    company_name: '',
    company_phone: '',
    phone: '',
    mobile: '',
    store_name: '',
    categories: '',
    country: '',
    city: '',
    state: '',
    postal_code: '',
    address1: '',
    address2: '',
    lat: undefined as number | undefined,
    lng: undefined as number | undefined,
    experience: null,
    description: '',
  };
  readonly profileSaving = signal<boolean>(false);

  // ── Étape contrat (signature OTP intégrée) ──────────────────────
  readonly contractLoading = signal<boolean>(false);
  readonly contractOtpSent = signal<boolean>(false);
  readonly contractOtpSending = signal<boolean>(false);
  readonly contractOtpCode = signal<string>('');
  readonly contractSigning = signal<boolean>(false);
  readonly contractSimOtp = signal<string | null>(null);

  // ── Étape revue + discussion ────────────────────────────────────
  readonly submitting = signal<boolean>(false);
  readonly messages = signal<TicketMessage[]>([]);
  readonly draftMessage = signal<string>('');
  readonly sendingMsg = signal<boolean>(false);
  readonly unread = signal<number>(0);

  private subs: Subscription[] = [];

  readonly stepStatus = (key: WizardStepKey): string => {
    const s = this.state();
    const step = s?.steps?.find((x) => x.key === key);
    return step?.status || 'todo';
  };

  readonly vendorStatus = computed(() => this.state()?.vendor_status || 'incomplete');
  readonly isApproved = computed(() => this.vendorStatus() === 'approved');
  readonly isRejected = computed(() => this.vendorStatus() === 'rejected');
  readonly isPending = computed(() => this.vendorStatus() === 'pending_review');

  ngOnInit(): void {
    this.loadState(true);
    // Temps réel : tout signal serveur (notification/ message) rafraîchit
    // l'état + le fil et fait remonter un badge non-lu si on n'est pas en revue.
    try {
      this.realtime.connect();
      this.subs.push(
        this.realtime.notifications$.subscribe(() => this.onRealtimeSignal()),
        this.realtime.messages$.subscribe(() => this.onRealtimeSignal()),
      );
    } catch {
      /* SSR / pas de SSE : on reste en mode pull. */
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => {
      try {
        s.unsubscribe();
      } catch {
        /* noop */
      }
    });
  }

  private onRealtimeSignal(): void {
    // Recharge l'état (statut) ; si on est sur l'écran revue, recharge le fil,
    // sinon on incrémente le badge non-lu et on auto-ouvre la revue.
    this.api.getState().subscribe({
      next: (st) => {
        this.state.set(st);
        if (this.activeStep() === 'review') {
          this.loadThread(false);
        } else {
          this.unread.update((n) => n + 1);
        }
      },
      error: () => {},
    });
  }

  private loadState(initial: boolean): void {
    if (initial) this.loading.set(true);
    this.api.getState().subscribe({
      next: (st) => {
        this.state.set(st);
        this.loading.set(false);
        if (st.phone && !this.otpPhone()) this.otpPhone.set(st.phone);
        if (initial) {
          const target = st.submitted ? 'review' : st.next_step || 'review';
          this.activeStep.set(target);
          if (target === 'review') this.loadThread(true);
        }
      },
      error: () => {
        this.loading.set(false);
        this.toast.danger('Impossible de charger votre progression.', 'Inscription');
      },
    });
  }

  // ── Navigation entre étapes ─────────────────────────────────────
  goTo(step: WizardStepKey): void {
    this.activeStep.set(step);
    if (step === 'review') {
      this.unread.set(0);
      this.loadThread(true);
    }
  }

  refresh(): void {
    this.loadState(false);
  }

  // ── Étape email ─────────────────────────────────────────────────
  resendEmail(): void {
    const email = this.state()?.email;
    if (!email || this.emailResending()) return;
    this.emailResending.set(true);
    this.authService.resendVerificationEmail(email).subscribe({
      next: () => {
        this.emailResending.set(false);
        this.toast.success('E-mail de vérification renvoyé.', 'Inscription');
      },
      error: () => {
        this.emailResending.set(false);
        this.toast.danger('Envoi impossible — réessayez.', 'Inscription');
      },
    });
  }

  // ── Étape téléphone (OTP WhatsApp) ──────────────────────────────
  sendOtp(): void {
    const phone = this.otpPhone().trim();
    if (!phone || this.otpSending()) return;
    this.otpSending.set(true);
    this.http
      .post(`${this.cfg.apiUrl}/2fa/phone-otp/request`, { phone, purpose: 'verify_phone' })
      .subscribe({
        next: () => {
          this.otpSending.set(false);
          this.otpSent.set(true);
          this.toast.success('Code envoyé par WhatsApp.', 'Vérification');
        },
        error: (err) => {
          this.otpSending.set(false);
          const msg = err?.status === 429 ? 'Trop de demandes — patientez.' : 'Envoi du code impossible.';
          this.toast.danger(msg, 'Vérification');
        },
      });
  }

  verifyOtp(): void {
    const phone = this.otpPhone().trim();
    const code = this.otpCode().trim();
    if (!phone || !code || this.otpVerifying()) return;
    this.otpVerifying.set(true);
    this.http
      .post(`${this.cfg.apiUrl}/2fa/phone-otp/verify`, { phone, code, purpose: 'verify_phone' })
      .subscribe({
        next: () => {
          this.otpVerifying.set(false);
          this.toast.success('Téléphone vérifié.', 'Vérification');
          this.otpCode.set('');
          this.loadState(false);
          this.goTo('profile');
        },
        error: () => {
          this.otpVerifying.set(false);
          this.toast.danger('Code invalide ou expiré.', 'Vérification');
        },
      });
  }

  // ── Étape profil ────────────────────────────────────────────────
  addressValue(): AddressValue {
    return {
      address_line: this.user.address1,
      city: this.user.city,
      postal_code: this.user.postal_code,
      governorate_code: this.user.state,
      country_code: this.user.country || 'TN',
      lat: this.user.lat,
      lng: this.user.lng,
    };
  }

  onAddressChange(v: AddressValue): void {
    if (v.address_line !== undefined) this.user.address1 = v.address_line || '';
    if (v.city !== undefined) this.user.city = v.city || '';
    if (v.postal_code !== undefined) this.user.postal_code = v.postal_code || '';
    if (v.governorate_code !== undefined) this.user.state = v.governorate_code || '';
    if (v.country_code !== undefined) this.user.country = v.country_code || '';
    if (v.lat !== undefined) this.user.lat = v.lat;
    if (v.lng !== undefined) this.user.lng = v.lng;
  }

  private isProfileValid(): boolean {
    if (this.user.user_type === 'company' && !(this.user.company_name || '').trim()) {
      this.toast.warning("Le nom de l'entreprise est requis.", 'Profil');
      return false;
    }
    if (this.user.user_type === 'individual' && !(this.user.store_name || '').trim()) {
      this.toast.warning('Le nom du magasin est requis.', 'Profil');
      return false;
    }
    if (!(this.user.country || '').trim() || !(this.user.city || '').trim() || !(this.user.address1 || '').trim()) {
      this.toast.warning('Adresse, ville et pays sont obligatoires.', 'Profil');
      return false;
    }
    return true;
  }

  saveProfile(): void {
    if (!this.isProfileValid() || this.profileSaving()) return;
    const phoneFromState = (this.state()?.phone || this.otpPhone() || '').trim();
    const payload: Record<string, unknown> = {
      user_type: this.user.user_type || 'individual',
      company_name: (this.user.company_name || '').trim() || null,
      company_phone: (this.user.company_phone || '').trim() || null,
      phone: (this.user.phone || '').trim() || phoneFromState || null,
      mobile: (this.user.mobile || '').trim() || phoneFromState || null,
      store_name: (this.user.store_name || '').trim() || null,
      categories: this.user.categories
        ? String(this.user.categories)
            .split(',')
            .map((c: string) => c.trim())
            .filter(Boolean)
        : [],
      country: (this.user.country || '').trim(),
      city: (this.user.city || '').trim(),
      state: (this.user.state || '').trim() || null,
      postal_code: (this.user.postal_code || '').trim() || null,
      address1: (this.user.address1 || '').trim(),
      address2: (this.user.address2 || '').trim() || null,
      description: (this.user.description || '').trim() || null,
      // experience n'est pas dans le formulaire — ne jamais envoyer null (422).
      experience: typeof this.user.experience === 'number' ? this.user.experience : null,
    };
    this.profileSaving.set(true);
    this.authService.CompleteSignup(payload).subscribe({
      next: () => {
        this.profileSaving.set(false);
        this.toast.success('Profil enregistré.', 'Profil');
        this.loadState(false);
        this.goTo('contract');
      },
      error: (err) => {
        this.profileSaving.set(false);
        const detail = err?.error?.detail;
        let msg = "Échec de l'enregistrement du profil.";
        if (typeof detail === 'string') msg = detail;
        else if (Array.isArray(detail) && detail[0]?.msg) {
          msg = detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join(' · ');
        }
        this.toast.danger(msg, 'Profil');
      },
    });
  }

  // ── Étape contrat (signature OTP intégrée, sans quitter le wizard) ──
  /** Étape 1 : crée la session de signature si besoin + envoie le code SMS. */
  sendContractOtp(): void {
    if (this.contractOtpSending()) return;
    this.contractOtpSending.set(true);
    this.api.sendContractOtp().subscribe({
      next: (res) => {
        this.contractOtpSending.set(false);
        if (res.signed) {
          this.toast.success('Contrat déjà signé.', 'Contrat');
          this.loadState(false);
          this.goTo('review');
          return;
        }
        this.contractOtpSent.set(true);
        this.contractSimOtp.set(res.simulation ? (res.otp_code || null) : null);
        this.toast.info(
          res.simulation
            ? 'Mode test : utilisez le code affiché ci-dessous.'
            : 'Un code de signature a été envoyé par e-mail.',
          'Contrat',
        );
      },
      error: (err) => {
        this.contractOtpSending.set(false);
        const detail = err?.error?.detail || err?.error?.message;
        this.toast.danger(
          detail || 'Envoi du code impossible. Réessayez dans un instant.',
          'Contrat',
        );
      },
    });
  }

  /** Étape 2 : vérifie le code et finalise la signature. */
  verifyContract(): void {
    if (this.contractSigning()) return;
    const code = (this.contractOtpCode() || '').trim();
    if (!code) {
      this.toast.danger('Saisissez le code reçu par e-mail.', 'Contrat');
      return;
    }
    this.contractSigning.set(true);
    this.api.verifyContractOtp(code).subscribe({
      next: (res) => {
        this.contractSigning.set(false);
        if (res.signed) {
          this.contractOtpSent.set(false);
          this.contractOtpCode.set('');
          this.contractSimOtp.set(null);
          this.toast.success('Contrat signé avec succès.', 'Contrat');
          this.loadState(false);
          this.goTo('review');
        }
      },
      error: (err) => {
        this.contractSigning.set(false);
        const detail = err?.error?.detail || err?.error?.message;
        this.toast.danger(detail || 'Code invalide, réessayez.', 'Contrat');
      },
    });
  }

  // ── Étape revue + soumission ────────────────────────────────────
  submit(): void {
    if (this.submitting()) return;
    this.submitting.set(true);
    this.api.submit().subscribe({
      next: (st) => {
        this.submitting.set(false);
        this.state.set(st);
        this.toast.success('Dossier soumis pour validation.', 'Inscription');
        this.loadThread(true);
      },
      error: (err) => {
        this.submitting.set(false);
        const missing = err?.error?.detail?.missing;
        if (Array.isArray(missing) && missing.length) {
          this.toast.warning('Étapes incomplètes : ' + missing.join(', '), 'Inscription');
        } else {
          this.toast.danger('Soumission impossible.', 'Inscription');
        }
        this.loadState(false);
      },
    });
  }

  // ── Discussion onboarding ───────────────────────────────────────
  private loadThread(reset: boolean): void {
    this.api.getThread().subscribe({
      next: (t) => {
        this.messages.set(t.messages || []);
        if (reset) this.unread.set(0);
      },
      error: () => {},
    });
  }

  sendMessage(): void {
    const body = this.draftMessage().trim();
    if (!body || this.sendingMsg()) return;
    this.sendingMsg.set(true);
    this.api.postMessage(body).subscribe({
      next: () => {
        this.sendingMsg.set(false);
        this.draftMessage.set('');
        this.loadThread(false);
      },
      error: () => {
        this.sendingMsg.set(false);
        this.toast.danger('Message non envoyé.', 'Inscription');
      },
    });
  }

  goToDashboard(): void {
    this.router.navigate(['/Accueil']);
  }

  isSupport(m: TicketMessage): boolean {
    return m.author_role === 'support_codincloud';
  }

  trackByMsg = (_: number, m: TicketMessage) => m.id;
}
